import { UserManager } from 'oidc-client-ts';
import { FC, PropsWithChildren, useMemo } from 'react';
import { AuthProvider, AuthProviderBaseProps } from 'react-oidc-context';
import { AccessTokenProvider } from './AccessTokenProvider';
import { useConfig } from '../../config';
import LoadingProgress from '../../layout/LoadingProgress';

const onSigninCallback: AuthProviderBaseProps['onSigninCallback'] = () => {
  // remove all OIDC query params from the URL
  // must use `replaceState` to block browser's back button
  window.history.replaceState({}, document.title, window.location.pathname);
};

export const OidcProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const { config } = useConfig();

  const authAuthority = `${config?.keyCloakUrl}/realms/${config?.keyCloakRealmId}`;

  const authConfig = {
    accessTokenExpiringNotificationTimeInSeconds: 3600, // refresh 1h before expiration
    authority: authAuthority,
    authorizationEndpoint: `${authAuthority}/protocol/openid-connect/auth`,
    clientId: process.env.REACT_APP_AUTH_CLIENT_ID ?? 'katalon-one',
    endSessionEndpoint: `${authAuthority}/protocol/openid-connect/logout`,
    revocationEndpoint: `${authAuthority}/protocol/openid-connect/revoke`,
    tokenEndpoint: `${authAuthority}/protocol/openid-connect/token`,
  };

  const userManager = useMemo(() => new UserManager({
    accessTokenExpiringNotificationTimeInSeconds:
authConfig.accessTokenExpiringNotificationTimeInSeconds,
    authority: authConfig.authority,
    client_id: authConfig.clientId,
    metadata: {
      authorization_endpoint: authConfig.authorizationEndpoint,
      end_session_endpoint: authConfig.endSessionEndpoint,
      revocation_endpoint: authConfig.revocationEndpoint,
      token_endpoint: authConfig.tokenEndpoint,
    },
    post_logout_redirect_uri: window.location.origin,
    redirect_uri: `${window.location.origin}/auth/callback`,
    // turn off built-in renew because it doesn't check refresh token TTL, see KU-136
    automaticSilentRenew: false,
  }), [JSON.stringify(config)]);

  if (!config) {
    return <LoadingProgress />;
  }

  let node = children;
  node = <AccessTokenProvider>{node}</AccessTokenProvider>;

  return (
    <AuthProvider userManager={userManager} onSigninCallback={onSigninCallback}>
      {node}
    </AuthProvider>
  );
};
